import {
  Dialog,
  Button,
  Typography,
  Box,
  TextField,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import React, { useEffect, useMemo, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { ManagementAdminsDialogStyle as styles } from "./ManagementAdminsDialogStyle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { pendingShipmentUpdatedFlagState } from "../recoil/atoms/pendingShipmentAtoms";
import { AdministratorListItem } from "./Table/AdministratorListTable";
import { baseSearchOptionsSelector } from "../recoil/selectors/baseSearchOptionsSelector";
import BaseFilterSelect from "./BaseFilter";

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdate: (updatedUser: AdministratorListItem, baseIds: number[]) => void;
  statusId: number;
  userInfo: AdministratorListItem;
};

const ManagementUsersDialog: React.FC<Props> = ({
  open,
  userInfo,
  onClose,
  onUpdate,
}) => {
  const setPendingShipmentUpdateFlag = useSetRecoilState(
    pendingShipmentUpdatedFlagState
  );

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);

  const [lastName, setLastName] = useState("");
  const [firstName, setFirstrName] = useState("");
  const [mailAddress, setMailAddress] = useState("");
  const [shopName, setShopName] = useState("");
  const [roomId, setRoomId] = useState("");
  const [driverName, setDriverName] = useState("");
  const [memo, setMemo] = useState("");
  const [editUserInfo, setEditUserInfo] = useState<AdministratorListItem>();
  const [baseName, setBaseName] = useState("");
  const [selectedBaseId, setSelectedBaseId] = useState<number | null>(null);
  const [selectedBaseIds, setSelectedBaseIds] = useState<number[]>([]);

  // 拠点データ取得
  const baseSearchOptions = useRecoilValue(baseSearchOptionsSelector);
  const baseOptions = useMemo(() => {
    return (
      baseSearchOptions?.map((base) => ({
        label: `${base.name}`,
        id: base.id!,
      })) ?? []
    );
  }, [baseSearchOptions]);

  const resetFields = () => {
    if (userInfo) {
      setLastName(userInfo.lastName ?? "");
      setFirstrName(userInfo.firstName ?? "");
      setMailAddress(userInfo.mailAddress ?? "");
      setRoomId(String(userInfo.roomId ?? ""));

      // エラー対策: 空配列をデフォルトに
      const base = (baseSearchOptions ?? []).find(
        (b) => b.name === userInfo.base
      );
      setSelectedBaseId(base?.id ?? null);
      setBaseName(userInfo.base ?? "");
      setSelectedBaseIds(userInfo.baseIds ?? []);

      setEditUserInfo(userInfo);
    }
  };

  // 初期値セット
  useEffect(() => {
    resetFields();
    if (!userInfo) return;
    setSelectedBaseIds(userInfo.baseIds ?? []);
  }, [userInfo]);

  // 入力欄データまとめ（拠点除く）
  const inputItems: [
    string,
    string,
    React.Dispatch<React.SetStateAction<string>>,
    keyof AdministratorListItem
  ][] = [
    ["性", lastName, setLastName, "lastName"],
    ["名", firstName, setFirstrName, "firstName"],
    ["メールアドレス", mailAddress, setMailAddress, "mailAddress"],
    ["roomID", roomId, setRoomId, "roomId"],
  ];

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
        <Dialog
          open={open}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") onClose();
          }}
          disableEnforceFocus
          maxWidth={false}
          fullWidth={false}
          PaperProps={{
            sx: {
              display: "flex",
              width: "700px",
              height: "450px",
              maxWidth: "none",
              maxHeight: "none",
              overflowY: "auto",
            },
          }}
        >
          <Box sx={styles.header}>
            <Typography sx={styles.headerText}>管理者情報更新</Typography>
            <Button
              onClick={() => {
                resetFields();
                onClose();
              }}
              sx={styles.closeButton}
            >
              ×
            </Button>
          </Box>

          <Box sx={styles.formArea}>
            {inputItems.slice(0, 3).map(([label, value, setter, key]) => (
              <Box key={label} sx={styles.inputRow}>
                <Typography sx={styles.label}>{label}：</Typography>
                <TextField
                  size="medium"
                  value={value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setter(newValue);
                    setEditUserInfo((prev) => ({
                      ...prev!,
                      [key]: newValue,
                    }));
                  }}
                  sx={{ width: "250px" }}
                />
              </Box>
            ))}

            {/* 4️⃣ 拠点: BaseFilterSelect */}
            <Box sx={styles.inputRow}>
              <Typography sx={styles.label}>拠点：</Typography>
              <BaseFilterSelect
                options={baseOptions}
                values={selectedBaseIds}
                onSelectMultiple={(newBaseIds) => {
                  setSelectedBaseIds(newBaseIds);
                  const baseNames = baseOptions
                    .filter((b) => newBaseIds.includes(b.id))
                    .map((b) => b.label)
                    .join("・");
                  setBaseName(baseNames);
                  setEditUserInfo((prev) => ({
                    ...prev!,
                    base: baseNames,
                    baseId: newBaseIds[0],
                  }));
                  setSelectedBaseIds(newBaseIds);
                }}
                isMultiple={true}
                sx={{ width: "250px" }}
                labelFontSize="0.9rem"
                onSelect={function (id: number | null): void {
                  throw new Error("Function not implemented.");
                }}
              />
            </Box>

            {inputItems.slice(3).map(([label, value, setter, key]) => (
              <Box key={label} sx={styles.inputRow}>
                <Typography sx={styles.label}>{label}：</Typography>
                <TextField
                  size="medium"
                  value={value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setter(newValue);
                    setEditUserInfo((prev) => ({
                      ...prev!,
                      [key]: newValue,
                    }));
                  }}
                  sx={{ width: "250px" }}
                />
              </Box>
            ))}
          </Box>

          <Button
            onClick={() => {
              onUpdate(editUserInfo!, selectedBaseIds);
            }}
            variant="contained"
            sx={styles.floatingRegisterButton}
            disabled={selectedBaseIds.length === 0}
          >
            更新
          </Button>

          <Snackbar
            open={toastOpen}
            autoHideDuration={4000}
            onClose={handleCloseToast}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert
              onClose={handleCloseToast}
              severity={toastSeverity}
              sx={{ width: "100%" }}
              elevation={6}
              variant="filled"
            >
              {toastMessage}
            </MuiAlert>
          </Snackbar>
        </Dialog>
      </LocalizationProvider>
    </>
  );
};

export default ManagementUsersDialog;
